import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { equityCurve } from "./data";
import { SectionHeading } from "./Engines";

function AnimatedNumber({ to, suffix = "", decimals = 0 }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0;
    const start = performance.now();
    const tick = (t) => {
      const p = Math.min((t - start) / 1600, 1);
      setV(to * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [to]);
  return (
    <span className="font-mono">
      {v.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const kpis = [
  { label: "Historical Accuracy", value: 60.4, suffix: "%+", decimals: 1 },
  { label: "AI Confidence Score", value: 84, suffix: "/100", decimals: 0 },
  { label: "Daily Signals", value: 17, suffix: "", decimals: 0 },
  { label: "Avg Risk / Reward", value: 2.4, suffix: "R", decimals: 1 },
];

export function Performance() {
  return (
    <section id="performance" className="relative py-24">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Performance"
          title="Measured, not promised."
          sub="Rolling results from the live decision log. Past performance is data, not a guarantee."
        />

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass grain rounded-2xl p-6"
          >
            <div className="mb-4 flex items-center justify-between">
              <p className="text-xs tracking-[0.2em] text-muted-foreground uppercase">
                Cumulative R · 8 weeks
              </p>
              <span className="flex items-center gap-2 text-[10px] tracking-[0.2em] text-muted-foreground uppercase">
                <span className="live-dot" /> Live
              </span>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={equityCurve} margin={{ left: -20, right: 8, top: 8 }}>
                  <defs>
                    <linearGradient id="pnlFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--gold)" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="var(--gold)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis
                    dataKey="d"
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tick={{ fill: "var(--muted-foreground)", fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      background: "var(--surface)",
                      border: "1px solid var(--border)",
                      borderRadius: 12,
                      color: "var(--foreground)",
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="pnl"
                    stroke="var(--gold)"
                    strokeWidth={2}
                    fill="url(#pnlFill)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-2">
            {kpis.map((k, i) => (
              <motion.div
                key={k.label}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass glass-hover flex flex-col justify-between rounded-2xl p-5"
              >
                <p className="text-xs tracking-[0.16em] text-muted-foreground uppercase">
                  {k.label}
                </p>
                <p className="mt-6 text-3xl font-semibold text-gold-gradient">
                  <AnimatedNumber to={k.value} suffix={k.suffix} decimals={k.decimals} />
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
